/* review-workspace-afterburner.jsx — live, mode-aware Afterburner results for
// RW-02b integration (🐓 2026-09-03): helpers rwAbRows/rwAbNumber/rwAbPercent/rwAbFindingRows renamed rwAb* — they collided with screens/afterburner.jsx top-level globals (vite-plugin-globals registers every top-level name).
// RW-02b integration (🐓): this view's status pill/run action carry data-afterburner-VIEW-status/-run — the contract hooks data-afterburner-status/-run stay exact-once on the Review tab's QA card (RW-02a), never duplicated by the canvas view.
   the review workspace. Reads only the eoa-qa status object owned by the shell;
   no fixture rows, simulated progress, or environment-mode guesses live here. */

const AB_SEVERITY_ORDER = ['blocker', 'high', 'medium', 'low'];
const AB_SEVERITY_LABEL = { blocker: 'Blocker', high: 'High', medium: 'Medium', low: 'Low' };
import { chaseFindingScope, chaseFindingProgress } from '../lib/review-chase-ui.js';

const AB_SEVERITY_COLOR = { blocker: 'var(--fail)', high: 'var(--brand-orange)', medium: 'var(--cond)', low: 'var(--queued)' };
// Same semantic channels, with readable foregrounds on the full-screen portal.
const AB_PORTAL_SEVERITY_STYLE = {
  blocker: { background: 'var(--fail-bg)', color: 'var(--ink)', borderColor: 'var(--fail)' },
  high: { background: 'var(--brand-orange)', color: 'var(--ink-on-brand)', borderColor: 'var(--brand-orange)' },
  medium: { background: 'var(--cond-bg)', color: 'var(--rw-cond-ink)', borderColor: 'var(--cond)' },
  low: { background: 'var(--queued-bg)', color: 'var(--ink-2)', borderColor: 'var(--queued)' },
};
const AB_PHASE_LABEL = {
  idle: 'Idle',
  queued: 'Queued',
  submitting_to_eoa: 'Submitting',
  fetching_renders: 'Fetching renders',
  stage_1_deterministic: 'Script checks',
  stage_2_perceptron: 'Perceptron',
  stage_3_claude: 'Claude diagnosis',
  stage_2_codex_checklist: 'Codex vision',
  stage_3_codex_coverage: 'Coverage',
  complete: 'Complete',
  skipped: 'Skipped',
  timeout: 'Timed out',
  error: 'Error',
  interrupted: 'Interrupted',
};
const AB_CROP_SIGNAL = {
  trusted_crop: 'Client chrome removed cleanly',
  missing_h3_chrome_box: 'Chrome box not found — crop bounds were not trusted',
  discarded_margin_content: 'Content sat in the discarded margin',
  possible_chrome_in_crop: 'Client chrome may remain in the crop (report-only)',
  chrome_box_untrusted: 'The recorded client-chrome box is not trusted',
  crop_helper_failed: 'The crop helper could not produce a trusted crop',
  screenshot_unavailable: 'The source screenshot was unavailable',
};
const AB_ZOOM = [0.5, 0.75, 1, 1.5, 2, 3, 4];
const abChaseUnavailable = chase => chase?.availabilityMessage || 'Chase is unavailable for this build. Check worker availability and your access.';
const ABIcons = window.BI;

// Retained-observation presenter contract, not the legacy job-level
// eoa-qa status or provider custody status. Every digest is server-owned.
const AB_BINDING_KEYS = ['schema', 'workspace_id', 'job_id', 'operation_id', 'revision', 'revision_digest',
  'html_digest', 'asset_manifest_digest', 'input_digest', 'observation_digest', 'result_digest'];
const AB_DIGEST = /^[a-f0-9]{64}$/;
function rwAbValidBinding(status) {
  const b = status && status.revision_binding;
  return !!b && typeof b === 'object' && !Array.isArray(b)
    && Object.keys(b).length === AB_BINDING_KEYS.length && AB_BINDING_KEYS.every(key => Object.hasOwn(b, key))
    && b.schema === 'AfterburnerRevisionBinding.v1'
    && ['workspace_id', 'job_id'].every(key => typeof b[key] === 'string' && b[key].length > 0 && b[key].length <= 200)
    && /^r(?:0|[1-9][0-9]{0,5})$/.test(b.revision || '')
    && ['operation_id', 'revision_digest', 'html_digest', 'asset_manifest_digest', 'input_digest', 'observation_digest', 'result_digest'].every(key => AB_DIGEST.test(b[key] || ''))
    && AB_BINDING_KEYS.filter(key => key !== 'schema').every(key => status[key] == null || status[key] === b[key]);
}
function rwAbEvidence(status, { jobId, workspaceId, session, revision, dirty = false, headMoved = false, refreshing = false } = {}) {
  const base = { state: 'unbound', current: false, revision: null, key: null,
    label: 'Unbound result · historical, not current revision QA',
    detail: 'This result has no verified binding to the current saved HTML and assets. Fix with Chase is disabled.' };
  if (!rwAbValidBinding(status)) return base;
  const b = status.revision_binding;
  if (!workspaceId || b.workspace_id !== workspaceId || b.job_id !== jobId) return base;
  const bound = { ...base, revision: b.revision, key: [b.operation_id, b.result_digest].join(':') };
  const historical = { ...bound, state: 'historical',
    label: 'Historical, not current revision QA', detail: dirty
      ? 'Draft changes are not covered by this saved-revision result. Fix with Chase is disabled.'
      : headMoved ? 'The saved head has moved. Refresh the current revision before using a finding.'
        : 'This result belongs to a different saved revision or source bundle. Fix with Chase is disabled.' };
  if (!session || session.job_id !== jobId || refreshing) return bound;
  if (dirty || headMoved || b.revision !== session.revision || b.revision_digest !== session.revision_digest) return historical;
  if (!revision || revision.revision !== session.revision || revision.revision_digest !== session.revision_digest
    || revision.html_digest !== session.projection?.html_digest || !AB_DIGEST.test(revision.asset_manifest_digest || '')) return bound;
  if (b.html_digest !== revision.html_digest || b.asset_manifest_digest !== revision.asset_manifest_digest) return historical;
  return { ...bound, state: 'current', current: true, label: 'Source-bound to saved revision ' + b.revision,
    detail: 'Exact saved HTML and assets match. This source binding is not a QA pass, approval or delivery certification.' };
}
function AbRevisionEvidence({ evidence, recorded }) {
  return <details className="w-evidence-compact" data-afterburner-revision-evidence={evidence.state}
    data-afterburner-result-key={evidence.key || undefined}>
    <summary><ABIcons.warn size={15} /> <b>{recorded ? 'Recorded · ' : ''}{evidence.label}</b>{evidence.revision && <> · {evidence.revision}</>} · Evidence</summary>
    <div>{evidence.detail}{recorded && <><br /><b>Recorded evidence — not live external-render proof.</b></>}
      {!evidence.current && <><br />Only a result bound to the current saved revision can provide a Chase fix.</>}
    </div>
  </details>;
}

function rwAbCodexCounts(status) {
  const summary = status.summary || {};
  const rows = status.stage2?.decisions;
  const judged = Array.isArray(rows) && rows.length ? rows.filter(row => row.source !== 'codex_skipped') : null;
  const returned = judged ? judged.length : Math.max(0, rwAbNumber(summary.codexDecisions) - rwAbNumber(summary.codexSkipped));
  const explicit = typeof summary.codexJudgedHits === 'number' && Number.isFinite(summary.codexJudgedHits);
  const flagged = explicit ? summary.codexJudgedHits : judged
    ? judged.filter(row => ['likely_broken', 'definitely_broken', 'borderline'].includes(row.classification)).length
    : rwAbNumber(summary.codexSkipped) === 0 ? rwAbNumber(summary.codexHits) : null;
  return { returned, flagged: flagged === null ? 'not measured' : Math.min(returned, Math.max(0, flagged)) };
}

function rwAbNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function rwAbPercent(value) {
  return Math.max(0, Math.min(100, rwAbNumber(value)));
}

function abKey(render) {
  return ((render && (render.clientId || render.clientName)) || '') + '::' + ((render && render.variant) || 'default');
}

/* Artifact truth owns the mode. Explicit legacy wins over stale/misleading
   counters; older legacy artifacts have no type and fall through to summary. */
function abIsCodex(status) {
  const type = status && status.stage2 && status.stage2.type;
  if (type === 'codex_checklist') return true;
  if (type === 'perceptron_triage') return false;
  const summary = (status && status.summary) || {};
  return rwAbNumber(summary.sentToCodex) > 0 || rwAbNumber(summary.codexDecisions) > 0;
}

function abArtifactRel(value) {
  if (!value) return null;
  let text = String(value).replace(/\\/g, '/');
  const marker = '/eoa-qa/';
  const markerIndex = text.indexOf(marker);
  if (markerIndex >= 0) return text.slice(markerIndex + 1);
  text = text.replace(/^\.?\/+/, '');
  if (text.startsWith('eoa-qa/')) return text;
  return 'eoa-qa/' + text;
}

function abAssetRel(asset) {
  if (!asset) return null;
  if (typeof asset === 'string') return abArtifactRel(asset);
  return abArtifactRel(asset.relPath || asset.path);
}

function abArtifactUrl(jobId, value) {
  const relative = abArtifactRel(value);
  return relative && window.GooseAPI && window.GooseAPI.artifactUrl
    ? window.GooseAPI.artifactUrl(jobId, relative)
    : null;
}

function rwAbFindingRows(status) {
  const diagnoses = (status && status.stage3 && status.stage3.diagnoses) || [];
  return [...diagnoses].sort((left, right) => {
    const leftIndex = AB_SEVERITY_ORDER.indexOf(left.severity || 'low');
    const rightIndex = AB_SEVERITY_ORDER.indexOf(right.severity || 'low');
    return (leftIndex === -1 ? 99 : leftIndex) - (rightIndex === -1 ? 99 : rightIndex);
  });
}

function abFindingSummary(finding) {
  return finding.issueSummary || finding.issue_summary || finding.summary || finding.reason || 'Diagnosis recorded.';
}

function abFindingFix(finding) {
  const value = finding.suggestedFix != null ? finding.suggestedFix
    : finding.suggested_fix != null ? finding.suggested_fix
      : finding.fix;
  if (Array.isArray(value)) return value.filter(Boolean).join(' ');
  return value ? String(value) : '';
}

function abChaseInstruction(finding, { render = {}, block, note = '' } = {}) {
  const context = [finding.clientName || finding.clientId || render.clientName || render.clientId,
    finding.variant || render.variant, finding.type || finding.issueType || finding.issue_type,
    block && `region ${block}`].filter(Boolean).join(' · ');
  const fix = abFindingFix(finding);
  return [note.trim(), abFindingSummary(finding), fix && `Suggested fix: ${fix}`, context && `(${context})`].filter(Boolean).join('\n');
}

function abRegionBlock(region) {
  if (!region) return null;
  if (typeof region === 'string') return region;
  return region.blockId || region.block || region.id || null;
}

function abFindingBlock(finding) {
  const direct = finding.blockId || finding.block || finding.sectionId;
  if (direct) return direct;
  const regions = finding.changedRegions || finding.changed_regions || finding.regions || [];
  return Array.isArray(regions) && regions.length ? abRegionBlock(regions[0]) : null;
}

function abResolvedBlock(finding, B) {
  const candidate = abFindingBlock(finding);
  return candidate && B && Array.isArray(B.blocks) && B.blocks.some((block) => block.id === candidate) ? candidate : null;
}

function rwAbRows(status, jobId) {
  const manifestRows = (status && status.manifest && status.manifest.renders) || [];
  const stage1Rows = (status && status.stage1 && status.stage1.renders) || [];
  const findings = rwAbFindingRows(status);
  const stage1ByKey = new Map(stage1Rows.map((row) => [abKey(row), row]));
  const findingsByKey = new Map();
  findings.forEach((finding) => {
    const key = abKey(finding);
    findingsByKey.set(key, [...(findingsByKey.get(key) || []), finding]);
  });
  return manifestRows.map((render) => {
    const qa = stage1ByKey.get(abKey(render));
    const screenshotRel = abAssetRel(render.screenshot) || abArtifactRel(qa && qa.screenshotPath);
    const thumbnailRel = abAssetRel(render.thumbnail) || abArtifactRel(qa && qa.thumbnailPath) || screenshotRel;
    const cropRel = abArtifactRel(qa && qa.cropPath);
    return {
      ...render,
      qa,
      qaStatus: qa ? (qa.status || 'pending') : 'pending',
      screenshotSrc: abArtifactUrl(jobId, screenshotRel),
      thumbnailSrc: abArtifactUrl(jobId, thumbnailRel),
      cropSrc: abArtifactUrl(jobId, cropRel),
      findings: findingsByKey.get(abKey(render)) || [],
      codexMode: abIsCodex(status),
    };
  });
}

function abStatusCard(status) {
  if (status === undefined) return { value: 'Checking Afterburner status…', status: 'loading' };
  if (status === null) return { value: 'Not materialized for this lane', status: 'not_materialized' };
  const state = status.status || 'not_started';
  if (state === 'not_started' || state === 'idle') return { value: 'Not run', status: state };
  return { value: AB_PHASE_LABEL[status.phase] || AB_PHASE_LABEL[state] || state, status: state };
}

function abCanRun() {
  const viewer = window.RBAC && window.RBAC.viewer && window.RBAC.viewer();
  return viewer ? !!(window.RBAC.requireRole && window.RBAC.requireRole('afterburner')) : true;
}

function AbStatusPill({ status, label }) {
  if (status === 'queued' || status === 'running') return <span className="w-abp run" data-afterburner-view-status={status}><i></i>{label || 'Running'}</span>;
  if (status === 'complete') return <span className="w-abp pass" data-afterburner-view-status={status}><i></i>{label || 'Complete'}</span>;
  if (status === 'skipped') return <span className="w-abp cond" data-afterburner-view-status={status}><ABIcons.warn size={10} />{label || 'Skipped'}</span>;
  if (status === 'timeout') return <span className="w-abp cond" data-afterburner-view-status={status}><ABIcons.warn size={10} />{label || 'Timed out'}</span>;
  if (status === 'error' || status === 'interrupted') return <span className="w-abp fail" data-afterburner-view-status={status}><i></i>{label || AB_PHASE_LABEL[status]}</span>;
  return <span className="w-abp ready" data-afterburner-view-status={status}>{label || 'Ready'}</span>;
}

function AbHeader({ job, c, isLive, status, codexMode, total, onRefresh, refreshing, refreshError, readAt, evidence }) {
  const card = abStatusCard(status);
  const state = card.status;
  const recorded = status?.recorded === true || status?.provider === 'recorded';
  const storedComplete = state === 'complete' && (!evidence?.current || recorded);
  const hasArtifacts = !!(status && (status.manifest || status.stage1 || status.stage2 || status.stage3 || status.summary));
  const legacy = hasArtifacts && !codexMode;
  const reportRel = status && status.artifacts && status.artifacts.report
    ? abArtifactRel(status.artifacts.report)
    : 'eoa-qa/eoa-visual-qa-report.md';
  const reportUrl = status && status.report ? abArtifactUrl(job.id, reportRel) : null;
  return (
    <React.Fragment>
      <div className="w-ab-top">
        <span className="w-ab-flame"><ABIcons.flame size={15} /></span>
        <b>Afterburner QA</b>
        <AbStatusPill status={storedComplete ? recorded ? 'recorded_complete' : 'historical_complete' : state} label={storedComplete ? recorded ? 'Recorded run complete' : 'Historical run complete' : state === 'loading' || state === 'not_materialized' || state === 'not_started' || state === 'idle' ? card.value : null} />
        {codexMode && <span className="w-ab-mode" title="Content gate: chrome-crop plus deterministic merge-token scan, then Codex visual checklist">content gate</span>}
        {legacy && <span className="w-ab-mode legacy" title="Recorded Perceptron-to-Claude artifact; displayed read-only">legacy artifact</span>}
        <span className="w-ab-meta w-meta">Email on Acid · {(c && c.name) || job.client || 'client'}{total ? ' · ' + total + ' renders' : ''}{readAt && state !== 'loading' ? ' · read ' + new Date(readAt).toLocaleTimeString() : ''}</span>
        <span className="w-grow" style={{ flex: 1 }}></span>
        {isLive && <button className="w-chip" title="Re-read the eoa-qa status" disabled={refreshing || status === undefined} onClick={onRefresh} data-afterburner-refresh="1">
          <ABIcons.retry size={11} /> <span className="w-lbl">{refreshing ? 'Reading…' : 'Refresh'}</span>
        </button>}
        {reportUrl && <a className="w-chip" href={reportUrl} target="_blank" rel="noopener noreferrer" title={reportRel}><ABIcons.doc size={11} /> <span className="w-lbl">eoa-qa report</span></a>}
      </div>
      {hasArtifacts && <AbRevisionEvidence evidence={evidence} recorded={status?.recorded === true || status?.provider === 'recorded'} />}
      {refreshError && <div className="w-abr bad" role="alert" style={{ margin: 12 }}><ABIcons.warn size={16} /><div><b>Refresh failed</b><span>{refreshError}</span></div></div>}
    </React.Fragment>
  );
}

function AbRenderStatus({ status }) {
  if (status === 'safe' || status === 'complete') return <span className="w-abp pass sm"><i></i>Clean</span>;
  if (status === 'suspicious') return <span className="w-abp cond sm"><ABIcons.warn size={9} />Suspect</span>;
  if (status === 'obvious_failure' || status === 'error') return <span className="w-abp fail sm"><i></i>Broken</span>;
  return <span className="w-abp ready sm">{status || 'pending'}</span>;
}

function AbThumbnail({ render }) {
  const name = render.clientName || render.clientId || 'Client render';
  const status = render.qaStatus || render.status;
  const ring = status === 'obvious_failure' ? 'var(--fail)' : status === 'suspicious' ? 'var(--cond)' : 'transparent';
  const source = render.thumbnailSrc || render.screenshotSrc;
  return (
    <div className="ab-thumb" title={source ? name + ' screenshot' : 'Screenshot unavailable'}
      style={ring !== 'transparent' ? { outline: '2px solid ' + ring, outlineOffset: '-2px' } : null}>
      {source
        ? <img data-render={'eoa-' + String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-')} className="rs-img" alt={name} src={source} />
        : <span className="w-meta" style={{ alignSelf: 'center', textAlign: 'center' }}>Screenshot unavailable</span>}
    </div>
  );
}

function abRectFor(value, width, height) {
  if (!value || typeof value !== 'object') return null;
  const raw = value.bounds || value.boundingBox || value.bounding_box || value.rect || value.box || value;
  let x = raw.x != null ? Number(raw.x) : Number(raw.left);
  let y = raw.y != null ? Number(raw.y) : Number(raw.top);
  let w = raw.w != null ? Number(raw.w) : raw.width != null ? Number(raw.width) : Number(raw.right) - x;
  let h = raw.h != null ? Number(raw.h) : raw.height != null ? Number(raw.height) : Number(raw.bottom) - y;
  if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return null;
  if (x > 1 || y > 1 || w > 1 || h > 1) {
    x /= Math.max(1, width); y /= Math.max(1, height); w /= Math.max(1, width); h /= Math.max(1, height);
  }
  x = Math.max(0, Math.min(1, x));
  y = Math.max(0, Math.min(1, y));
  w = Math.max(0, Math.min(1 - x, w));
  h = Math.max(0, Math.min(1 - y, h));
  return w && h ? { x, y, w, h } : null;
}

/* Boxes come only from coordinates recorded on a diagnosis/changed region.
   Current pipeline rows have none, so the viewer truthfully shows the finding
   list without inventing a location. */
function abvBoxes(render) {
  const dimensions = (render.screenshot && render.screenshot.dimensions) || { width: 600, height: 980 };
  const boxes = [];
  (render.findings || []).forEach((finding, findingIndex) => {
    const changed = finding.changedRegions || finding.changed_regions || finding.regions;
    const candidates = Array.isArray(changed) && changed.length ? changed : [finding];
    candidates.forEach((region) => {
      const rect = abRectFor(region, dimensions.width, dimensions.height);
      if (!rect) return;
      const block = abRegionBlock(region) || abFindingBlock(finding);
      boxes.push({
        ...rect,
        findingIndex,
        block,
        label: (region && (region.label || region.name)) || block || finding.type || 'recorded region',
        sev: AB_SEVERITY_ORDER.includes(finding.severity) ? finding.severity : 'low',
        type: finding.type || finding.issueType || finding.issue_type || 'finding',
        note: abFindingSummary(finding),
      });
    });
  });
  return boxes;
}

function AbRenderView({ renders, index, setIndex, accent, chase, B, onClose, onOpenBlock, decisions, evidence, recorded }) {
  const render = renders[index];
  if (!render) return null;
  const [zoomIndex, setZoomIndex] = React.useState(2);
  const [fit, setFit] = React.useState(true);
  const [drag, setDrag] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [showBoxes, setShowBoxes] = React.useState(true);
  const [note, setNote] = React.useState('');
  const [sent, setSent] = React.useState(null);
  const [chaseError, setChaseError] = React.useState(null);
  const [imageFailed, setImageFailed] = React.useState(false);
  const dialogRef = React.useRef(null);
  const holdRef = React.useRef(null);
  const [hold, setHold] = React.useState({ width: 0, height: 0 });
  const findings = render.findings || [];
  const boxes = abvBoxes(render);
  const source = render.screenshotSrc || render.thumbnailSrc;
  const dimensions = render.screenshotSrc && render.screenshot && render.screenshot.dimensions
    ? render.screenshot.dimensions
    : render.thumbnail && render.thumbnail.dimensions
      ? render.thumbnail.dimensions
      : { width: 600, height: 980 };
  const paperWidth = Math.max(1, rwAbNumber(dimensions.width) || 600);
  const paperHeight = Math.max(1, rwAbNumber(dimensions.height) || 980);
  const zoom = AB_ZOOM[zoomIndex];
  const fitZoom = hold.width && hold.height
    ? Math.max(0.05, Math.min((hold.width - 48) / paperWidth, (hold.height - 48) / paperHeight))
    : 0.6;
  const effectiveZoom = fit ? fitZoom : zoom;
  const qa = render.qa || {};
  const decision = decisions && decisions.get ? decisions.get(abKey(render)) : null;
  const votes = decision && decision.votes;
  const unmeasured = render.codexMode && !render.qa;
  const untrusted = render.codexMode && qa.cropTrusted === false;
  const skipped = render.codexMode && decision && decision.source === 'codex_skipped';
  const canFix = !!evidence?.current;
  const fixContext = React.useRef(null);
  fixContext.current = canFix ? evidence.key : null;

  React.useEffect(() => {
    const previous = document.activeElement;
    if (dialogRef.current) dialogRef.current.focus();
    return () => {if (previous && previous.focus) previous.focus();};
  }, []);

  React.useEffect(() => {
    const element = holdRef.current;
    if (!element) return undefined;
    const measure = () => setHold({ width: element.clientWidth, height: element.clientHeight });
    measure();
    if (!window.ResizeObserver) return undefined;
    const observer = new window.ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    setSelected(null); setSent(null); setChaseError(null); setNote(''); setFit(true); setImageFailed(false);
  }, [index, source]);

  React.useEffect(() => {
    const onKey = (event) => {
      if (event.target && /INPUT|TEXTAREA/.test(event.target.tagName)) {
        if (event.key === 'Escape') event.target.blur();
        return;
      }
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight') setIndex((value) => Math.min(renders.length - 1, value + 1));
      else if (event.key === 'ArrowLeft') setIndex((value) => Math.max(0, value - 1));
      else if (event.key === '+' || event.key === '=') {setFit(false);setZoomIndex((value) => Math.min(AB_ZOOM.length - 1, value + 1));}
      else if (event.key === '-') {setFit(false);setZoomIndex((value) => Math.max(0, value - 1));}
      else if (event.key === '0') setFit(true);
      else if (event.key.toLowerCase() === 'o') setShowBoxes((value) => !value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [renders.length, onClose, setIndex]);

  React.useEffect(() => {
    const element = holdRef.current;
    if (!element || fit) return;
    element.scrollLeft = (element.scrollWidth - element.clientWidth) / 2;
  }, [effectiveZoom, fit]);

  const onWheel = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    setFit(false);
    setZoomIndex((value) => Math.max(0, Math.min(AB_ZOOM.length - 1, value + (event.deltaY < 0 ? 1 : -1))));
  };
  const startDrag = (event) => {
    if (event.button !== 0 || !holdRef.current) return;
    setDrag({ x: event.clientX, y: event.clientY, left: holdRef.current.scrollLeft, top: holdRef.current.scrollTop });
  };
  const moveDrag = (event) => {
    if (!drag || !holdRef.current) return;
    holdRef.current.scrollLeft = drag.left - (event.clientX - drag.x);
    holdRef.current.scrollTop = drag.top - (event.clientY - drag.y);
  };
  const selectedFinding = selected != null ? findings[selected] : null;
  const selectedBlock = selectedFinding && abResolvedBlock(selectedFinding, B);
  const selectedScope = chaseFindingScope(B?.blocks, selectedBlock);
  const progress = sent?.finding === selected && sent.block === selectedBlock && sent.context === fixContext.current ? chaseFindingProgress(chase, sent.operation_id) : null;
  const askChase = async () => {
    if (!canFix || !selectedFinding || !selectedScope || !chase || !chase.wired || chase.busy || chase.disabled || chase.acceptBlocked || progress === 'Patch staged') return;
    const context = fixContext.current;
    setChaseError(null);
    const instruction = abChaseInstruction(selectedFinding, { render, block: selectedBlock, note });
    const result = await chase.ask(instruction, selectedScope);
    if (!context || fixContext.current !== context) return;
    if (result?.operation_id) {setSent({ finding: selected, block: selectedBlock, operation_id: result.operation_id, context });setNote('');}
    else setChaseError({ finding: selected, context, message: result?.error?.title || 'Chase did not accept this request. Check scope and worker availability, then retry.' });
  };

  return (
    <div className="w-abv w-review-theme" ref={dialogRef} role="dialog" aria-modal="true" aria-label={(render.clientName || render.clientId || 'Client render') + ' Afterburner viewer'} tabIndex="-1" data-afterburner-render-view={abKey(render)}>
      <header className="w-abv-h">
        <div className="w-abv-t"><b>{render.clientName || render.clientId}</b><span>{(render.clientFamily || 'client') + (render.variant ? ' · ' + render.variant : '')} · {paperWidth}×{paperHeight}px</span></div>
        <span className="w-grow" style={{ flex: 1 }}></span>
        <span className="w-abd"><button className={showBoxes ? 'on' : ''} onClick={() => setShowBoxes(true)} title="Show recorded regions (o)">overlay</button><button className={!showBoxes ? 'on' : ''} onClick={() => setShowBoxes(false)} title="Hide recorded regions (o)">clean</button></span>
        <span className="w-abv-zoom">
          <button onClick={() => {setFit(false);setZoomIndex((value) => Math.max(0, value - 1));}} title="Zoom out (−)"><ABIcons.minus size={13} /></button>
          <button className="w-abv-zn" onClick={() => setFit(true)} title="Fit to window (0)">{Math.round(effectiveZoom * 100)}%</button>
          <button onClick={() => {setFit(false);setZoomIndex((value) => Math.min(AB_ZOOM.length - 1, value + 1));}} title="Zoom in (+)"><ABIcons.add size={13} /></button>
        </span>
        <span className="w-abv-pg"><button onClick={() => setIndex(Math.max(0, index - 1))} disabled={index === 0} title="Previous render (←)"><ABIcons.chevron size={14} style={{ transform: 'rotate(180deg)' }} /></button><span>{index + 1} / {renders.length}</span><button onClick={() => setIndex(Math.min(renders.length - 1, index + 1))} disabled={index === renders.length - 1} title="Next render (→)"><ABIcons.chevron size={14} /></button></span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><ABIcons.x size={14} /> Close</button>
      </header>

      <div className="w-abv-body">
        <div className="w-abv-stage" ref={holdRef} onWheel={onWheel} onPointerDown={startDrag} onPointerMove={moveDrag}
          onPointerUp={() => setDrag(null)} onPointerLeave={() => setDrag(null)} style={{ cursor: drag ? 'grabbing' : 'grab' }}>
          <div className="w-abv-sizer" style={{ width: Math.round(paperWidth * effectiveZoom), height: Math.round(paperHeight * effectiveZoom) }}>
            <div className="w-abv-paper" style={{ width: paperWidth, height: paperHeight, transform: 'scale(' + effectiveZoom + ')' }}>
              <div className="w-abv-shot" style={{ display: 'grid', placeItems: 'center', color: 'var(--ink-on-brand)', background: '#fff' }}>
                {source && !imageFailed
                  ? <img src={source} alt={render.clientName || render.clientId || 'Client render'} draggable="false" onError={() => setImageFailed(true)} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'top center' }} />
                  : <span style={{ fontSize: 13 }}>Render image unavailable</span>}
              </div>
              {showBoxes && boxes.map((box, boxIndex) => <button key={boxIndex} className={'w-abv-box ' + box.sev + (selected === box.findingIndex ? ' sel' : '')}
                style={{ left: box.x * paperWidth, top: box.y * paperHeight, width: box.w * paperWidth, height: box.h * paperHeight }}
                onPointerDown={(event) => event.stopPropagation()} onClick={() => setSelected(selected === box.findingIndex ? null : box.findingIndex)}>
                <span className="w-abv-tag" style={{ transform: 'scale(' + 1 / effectiveZoom + ')' }}>{box.type}</span>
              </button>)}
            </div>
          </div>
          {!boxes.length && showBoxes && <div className="w-abv-clean"><ABIcons.check size={13} /> No recorded region coordinates for this render</div>}
        </div>

        <aside className="w-abv-side">
          <AbRevisionEvidence evidence={evidence} recorded={recorded} />
          {(unmeasured || untrusted || skipped || (qa.cropSignals && qa.cropSignals.length) || votes || render.cropSrc) && <div className="w-abv-gate">
            {(unmeasured || untrusted || skipped) && <div className="w-abv-unseen"><ABIcons.warn size={14} /><span><b>The gate could not see this render.</b>{unmeasured ? ' No joined stage-1 measurement has been recorded yet.' : untrusted ? ' The crop was not trusted, so it never reached the checklist.' : ' The checklist was skipped or returned insufficient ballots.'} {unmeasured ? 'Pending measurement — not clean.' : 'Routed to review — not a pass.'}</span></div>}
            {(!!(qa.cropSignals && qa.cropSignals.length) || render.cropSrc) && <div className="w-abv-sig"><span className="w-ab-vl">Crop</span>{(qa.cropSignals || []).map((signal) => <span key={signal} className={'w-abv-sigc' + (signal === 'trusted_crop' ? ' ok' : '')} title={AB_CROP_SIGNAL[signal] || signal}>{signal}</span>)}{render.cropSrc && <a className="w-abv-cp" href={render.cropSrc} target="_blank" rel="noopener noreferrer" title="Open the image the checklist received">model crop</a>}</div>}
            {votes && votes.configured > 1 && <div className="w-ab-votes"><span className="w-ab-vl">Vote-of-{votes.configured}</span><span className="w-ab-ball">{Array.from({ length: votes.configured }).map((_, ballotIndex) => {const ballot = (votes.perBallot || [])[ballotIndex];return <i key={ballotIndex} className={ballot == null ? 'none' : ballot === 'ok' ? '' : 'hit'} title={ballot == null ? 'no ballot returned' : ballot}></i>;})}</span>{votes.carried && <span className="w-ab-carried">{votes.carried}</span>}{(votes.minorityOnly || []).map((minority) => <span key={minority} className="w-ab-minor" title="Minority-only signal; it did not carry the vote">{minority}</span>)}</div>}
          </div>}
          <div className="w-abv-sh"><ABIcons.warn size={13} style={{ color: findings.length ? 'var(--cond)' : 'var(--pass)' }} /><b>Recorded findings</b><span className="w-grow" style={{ flex: 1 }}></span><span className="w-meta">{findings.length}</span></div>
          <div className="w-abv-list">
            {findings.map((finding, findingIndex) => {
              const severity = AB_SEVERITY_ORDER.includes(finding.severity) ? finding.severity : 'low';
              const block = abResolvedBlock(finding, B);
              return <button key={findingIndex} className={'w-abv-item' + (selected === findingIndex ? ' sel' : '')} onClick={() => setSelected(selected === findingIndex ? null : findingIndex)}><span className="w-abv-sev" style={AB_PORTAL_SEVERITY_STYLE[severity]}>{AB_SEVERITY_LABEL[severity]}</span><span className="w-abv-il"><b>{block || finding.clientName || finding.clientId || 'Finding'}</b><code>{finding.type || finding.issueType || finding.issue_type || 'diagnosis'}</code><i>{abFindingSummary(finding)}</i></span></button>;
            })}
            {!findings.length && <div className="w-abv-none">Afterburner recorded no finding for this render.</div>}
          </div>

          {selectedFinding && <div className="w-abv-ask">
            <div className="w-abv-ah"><span className="w-spark"><ABIcons.jet size={11} /></span><b>{selectedBlock ? 'Send to Chase' : 'Finding context'}</b><span className="w-grow" style={{ flex: 1 }}></span>{selectedBlock && <button className="w-cm-act" onClick={() => onOpenBlock && onOpenBlock(selectedBlock)}>open block</button>}</div>
            {selectedBlock ? <React.Fragment>
              <p className="w-abv-ap">Recorded finding → block <code>{selectedBlock}</code>. Chase stages a diff; nothing changes until it is accepted.</p>
              <textarea className="w-in" rows="3" disabled={!canFix} value={note} onChange={(event) => setNote(event.target.value)} placeholder={'What to fix in ' + selectedBlock + '… (blank sends the finding as-is)'} />
              {!chase.wired && <p className="w-abv-ap" data-afterburner-chase="unwired">{abChaseUnavailable(chase)}</p>}
              {!selectedScope && <p className="w-abv-ap">This is a derived overlay, not an authored region. Select an authored block or explicitly request Whole file in Chase.</p>}
              {progress && progress !== 'Patch staged' && <p role="status" data-afterburner-chase-operation={sent.operation_id} className="w-abv-ap">{progress}</p>}
              {chaseError?.finding === selected && chaseError.context === fixContext.current && <p role="alert">{chaseError.message}</p>}
              <div className="w-abv-aa">{progress === 'Patch staged'
                ? <span role="status" data-afterburner-chase-operation={sent.operation_id} className="w-abp pass"><i/>Staged on {B?.blocks?.find(block => block.id === selectedBlock)?.label || selectedBlock}</span>
                : <button className="btn btn-primary btn-sm" disabled={!canFix || !selectedScope || !chase.wired || chase.busy || chase.disabled || chase.acceptBlocked} onClick={askChase} title={!canFix ? evidence.detail : !chase.wired ? abChaseUnavailable(chase) : 'Ask Chase to stage a patch'}><ABIcons.jet size={12} /> {chase.busy ? 'Drafting…' : 'Ask Chase to fix'}</button>}
                <span className="w-grow" style={{flex:1}}/><span className="w-meta">{selectedFinding.severity || 'ungraded'}</span>
              </div>
            </React.Fragment> : <p className="w-abv-ap">This eoa-qa diagnosis does not record a workspace block identity, so no block action or Chase patch is inferred.</p>}
          </div>}
          {selected == null && !!findings.length && <div className="w-abv-hint">Select a recorded finding to inspect its workspace linkage.</div>}
          <div className="w-abv-keys">⌘/ctrl + wheel zoom · drag to pan · <b>0</b> fit · <b>o</b> overlay · <b>←→</b> renders · <b>esc</b> close</div>
        </aside>
      </div>
    </div>
  );
}

function RwAfterburnerLegacyView({ job, c, isLive, B, chase, afterburnerStatus, onRunAfterburner, onViewDiff,
  editorSession, draftDirty = false, revisionHeadMoved = false, sessionRefreshing = false,
  afterburnerContextKey = '', afterburnerWorkspaceId = null }) {
  const scopeKey = JSON.stringify([afterburnerContextKey, afterburnerWorkspaceId, job.id]);
  const contextKey = JSON.stringify([scopeKey, editorSession?.revision, editorSession?.revision_digest,
    editorSession?.projection?.html_digest, draftDirty, revisionHeadMoved, sessionRefreshing]);
  const [loaded, setLoaded] = React.useState({ scope: scopeKey, data: afterburnerStatus });
  const status = loaded.scope === scopeKey ? loaded.data : undefined;
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState(null);
  const [fixError, setFixError] = React.useState(null);
  const [readAt, setReadAt] = React.useState(null);
  const [family, setFamily] = React.useState('all');
  const [view, setView] = React.useState(null);
  const refreshRequest = React.useRef({ context: contextKey, sequence: 0 });
  refreshRequest.current.context = contextKey;
  const [revisionLoad, setRevisionLoad] = React.useState(null);
  const binding = rwAbValidBinding(status) ? status.revision_binding : null;
  const evidenceKey = binding && editorSession && !sessionRefreshing
    && binding.job_id === job.id && binding.workspace_id === afterburnerWorkspaceId
    ? JSON.stringify([contextKey, binding.operation_id, binding.result_digest]) : null;
  const evidenceRequest = React.useRef(null);
  evidenceRequest.current = evidenceKey;
  React.useEffect(() => {
    if (!evidenceKey) return;
    let alive = true;
    setRevisionLoad({ key: evidenceKey, value: null });
    if (!window.GooseAPI?.readRevision) return () => { alive = false; };
    Promise.resolve().then(() => alive && evidenceRequest.current === evidenceKey
      ? window.GooseAPI.readRevision(job.id, editorSession.revision) : null).then(value => {
      if (alive && evidenceRequest.current === evidenceKey) setRevisionLoad({ key: evidenceKey, value });
    }, () => {
      if (alive && evidenceRequest.current === evidenceKey) setRevisionLoad({ key: evidenceKey, value: null });
    });
    return () => { alive = false; };
  }, [evidenceKey]);
  const evidence = rwAbEvidence(status, { jobId: job.id, workspaceId: afterburnerWorkspaceId, session: editorSession,
    revision: revisionLoad?.key === evidenceKey ? revisionLoad.value : null, dirty: draftDirty,
    headMoved: revisionHeadMoved, refreshing: sessionRefreshing });
  const canFix = evidence.current && status?.status === 'complete';

  React.useLayoutEffect(() => {
    refreshRequest.current.sequence += 1;
    setLoaded({ scope: scopeKey, data: afterburnerStatus });
    setRefreshing(false);
    setRefreshError(null);
    setReadAt(afterburnerStatus === undefined ? null : Date.now());
    setFamily('all');
    setView(null);
    return () => {refreshRequest.current.sequence += 1;};
  }, [afterburnerStatus, scopeKey]);
  React.useLayoutEffect(() => {
    refreshRequest.current.sequence += 1;
    setRefreshing(false); setRefreshError(null); setView(null);
    return () => { refreshRequest.current.sequence += 1; };
  }, [contextKey]);

  const refresh = async () => {
    if (!isLive || status === undefined || refreshing) return;
    if (!(window.GooseAPI && window.GooseAPI.afterburnerStatus)) {
      setRefreshError('Afterburner status API is unavailable.');
      return;
    }
    const jobId = job.id;
    const sequence = ++refreshRequest.current.sequence;
    setRefreshing(true); setRefreshError(null);
    try {
      const next = await window.GooseAPI.afterburnerStatus(jobId);
      if (refreshRequest.current.context !== contextKey || refreshRequest.current.sequence !== sequence) return;
      setLoaded({ scope: scopeKey, data: next || null });
      setReadAt(Date.now());
      setFamily('all');
      setView(null);
    } catch (error) {
      if (refreshRequest.current.context !== contextKey || refreshRequest.current.sequence !== sequence) return;
      setRefreshError((error && error.message) || 'Could not read eoa-qa status.');
    } finally {
      if (refreshRequest.current.context === contextKey && refreshRequest.current.sequence === sequence) setRefreshing(false);
    }
  };

  const card = abStatusCard(status);
  const canRun = abCanRun() && (card.status === 'not_started' || card.status === 'idle');
  if (status === undefined || status === null || card.status === 'not_started' || card.status === 'idle') {
    return <div className="w-ab" data-afterburner-view="empty"><AbHeader job={job} c={c} isLive={isLive} status={status} codexMode={false} total={0} onRefresh={refresh} refreshing={refreshing} refreshError={refreshError} readAt={readAt} evidence={evidence} /><div className="w-ab-empty"><ABIcons.flame size={24} /><b>{card.value}</b><span>Revision-specific Afterburner launch is unavailable here. Open job Afterburner uses the legacy job-level pipeline; it does not validate the edited revision. Existing job-level results remain viewable, not current revision QA.</span>{canRun && <button className="btn btn-primary" onClick={onRunAfterburner} data-afterburner-view-run="1">Open job Afterburner</button>}</div></div>;
  }

  const codexMode = abIsCodex(status);
  const summary = status.summary || {};
  const eoa = status.eoa || status.eoaResult || {};
  const renders = rwAbRows(status, job.id);
  const findings = rwAbFindingRows(status);
  const total = rwAbNumber(summary.totalRenders != null ? summary.totalRenders : eoa.screenshotCount != null ? eoa.screenshotCount : renders.length);
  const human = findings.filter((finding) => finding.needsHumanReview || finding.humanReview).length;
  const untrusted = codexMode ? rwAbNumber(summary.untrustedCrops) : 0;
  const skipped = codexMode ? rwAbNumber(summary.codexSkipped) : 0;
  const routed = untrusted + skipped;
  const mergeHits = codexMode ? rwAbNumber(summary.mergeTokenHits) : 0;
  const mergeStatus = String(summary.mergeTokenScanStatus || 'not_run');
  const mergeUnavailable = mergeStatus === 'unavailable';
  const mergeComplete = mergeStatus === 'complete';
  const verdict = String(summary.finalVerdict || status.finalVerdict || '').toLowerCase();
  const running = status.status === 'queued' || status.status === 'running';
  const phase = AB_PHASE_LABEL[status.phase] || status.phase || 'Idle';
  const reason = status.error || status.reason || eoa.reason || (status.eoaResult && status.eoaResult.reason) || '';
  const hasResults = total > 0 || renders.length > 0 || findings.length > 0;
  const codexCounts = rwAbCodexCounts(status);
  const funnel = codexMode
    ? [[total, 'Script checks', 'all renders'], [Math.max(0, total - untrusted), 'Chrome-cropped', 'client chrome removed'], [codexCounts.returned, 'Codex vision', 'decisions returned'], [codexCounts.flagged, 'Flagged', 'majority verdict']]
    : [[total, 'Script checks', 'all renders'], [rwAbNumber(summary.sentToPerceptron), 'Perceptron Mk1', 'suspicious'], [rwAbNumber(summary.sentToClaude), 'Claude diagnosis', 'likely broken'], [human, 'Human review', 'low-confidence']];
  const decisions = new Map(((status.stage2 && status.stage2.decisions) || []).map((decision) => [abKey(decision), decision]));
  const severityCounts = AB_SEVERITY_ORDER.map((severity) => [severity, findings.filter((finding) => (finding.severity || 'low') === severity).length]);
  const families = ['all', ...Array.from(new Set(renders.map((render) => render.clientFamily || 'client')))];
  const shown = renders.filter((render) => family === 'all' || (render.clientFamily || 'client') === family);
  const blockLabel = (id) => ((B.blocks || []).find((block) => block.id === id) || {}).label || id;
  const openBlock = (id) => {
    if (!id) return;
    if (onViewDiff) onViewDiff(id);
    else if (B && B.setSel) B.setSel(id);
  };

  return (
    <div className="w-ab" data-afterburner-view="results" data-afterburner-mode={codexMode ? 'codex' : 'legacy'}>
      <AbHeader job={job} c={c} isLive={isLive} status={status} codexMode={codexMode} total={total} onRefresh={refresh} refreshing={refreshing} refreshError={refreshError} readAt={readAt} evidence={evidence} />
      <div className="w-ab-scroll">
        {running && <React.Fragment><div className="w-abbar"><span style={{ width: rwAbPercent(status.progress) + '%' }}></span></div><div className="w-ab-log"><div className="w-ab-line"><span className="ts">{String(Math.round(rwAbPercent(status.progress))).padStart(2, '0')}%</span><span>{phase}</span></div>{status.startedAt && <div className="w-ab-line"><span className="ts">start</span><span>{new Date(status.startedAt).toLocaleTimeString()}</span></div>}<div className="w-ab-line"><span className="cur">█</span></div></div></React.Fragment>}
        {!running && reason && <div className={'w-abr' + (status.status === 'error' ? ' bad' : '')}><ABIcons.warn size={17} /><div><b>{phase}</b><span>{reason}</span></div></div>}

        {!codexMode && hasResults && <div className="w-callout info" style={{ marginBottom: 12 }} data-afterburner-legacy="read-only"><ABIcons.warn size={15} /><span><b>Legacy artifact · read-only.</b> This recorded run used Perceptron Mk1 triage followed by Claude diagnosis. Crop trust, merge-token scan, and Codex ballots are intentionally not inferred.</span></div>}
        {hasResults && <React.Fragment>
          {verdict && <div className={'w-ab-verdict ' + (verdict === 'fail' ? 'fail' : evidence.current && !status.recorded && verdict === 'pass' ? 'pass' : 'review')}><ABIcons.warn size={16} /><b>Recorded {verdict === 'pass' ? 'PASS' : verdict === 'fail' ? 'FAIL' : 'REVIEW'}</b><span>{verdict === 'review' ? (codexMode ? routed ? 'Unmeasured is not clean — ' + routed + ' render' + (routed === 1 ? '' : 's') + ' routed to review.' : findings.length ? findings.length + ' recorded coverage finding' + (findings.length === 1 ? '' : 's') + ' require review.' : 'The recorded gate evidence requires review.' : human + ' finding' + (human === 1 ? '' : 's') + ' need a human call.') : verdict === 'fail' ? 'Recorded defects require attention.' : 'The recorded report marked measured renders clean; this is not approval or current revision certification.'}</span></div>}
          <div className="w-casc" style={{ flexShrink: 0 }} data-afterburner-funnel={codexMode ? 'codex' : 'legacy'}>{funnel.map(([value, label, detail]) => <div key={label} className="w-casc-n" data-afterburner-stage={label} style={{ '--fw': Math.max(6, Math.round(rwAbNumber(value) / Math.max(1, rwAbNumber(funnel[0][0])) * 100)) + '%' }}><b>{value}</b><span>{label}</span><i>{detail}</i><em></em></div>)}</div>
          {codexMode && <div className="w-ab-outside" data-afterburner-outside="1"><div className={'w-ab-ax' + (mergeHits ? ' bad' : mergeComplete ? ' ok' : ' warn')} title="The deterministic HTML scan runs once per build, outside the render funnel"><span className="w-ab-ax-n">{mergeHits}</span><span className="w-ab-ax-t"><b>Merge tokens{mergeHits ? '' : mergeComplete ? ' clear' : mergeUnavailable ? ' unavailable' : mergeStatus === 'not_run' ? ' not run' : ' pending'}</b><i>{mergeStatus === 'not_run' ? 'scan not run' : mergeUnavailable ? 'scan unavailable — review required' : mergeHits ? 'unresolved in the shipped HTML' : mergeComplete ? 'none unresolved' : 'scan status: ' + mergeStatus}{rwAbNumber(summary.mergeTokenAllowed) ? ' · ' + summary.mergeTokenAllowed + ' allowlisted' : ''}</i></span></div><div className={'w-ab-ax' + (routed ? ' warn' : ' ok')} title="Fail-closed: unmeasurable renders route to review and are not findings"><span className="w-ab-ax-n">{routed}</span><span className="w-ab-ax-t"><b>Routed to review</b><i>{untrusted} untrusted crop{untrusted === 1 ? '' : 's'} · {skipped} codex skipped — unmeasurable, not flagged</i></span></div></div>}
        </React.Fragment>}

        {!!findings.length && <div className="w-ab-sev">{severityCounts.map(([severity, count]) => <span key={severity} className="w-ab-sevchip"><i style={{ background: AB_SEVERITY_COLOR[severity] }}></i>{AB_SEVERITY_LABEL[severity]}<b style={{ color: AB_SEVERITY_COLOR[severity] }}>{count}</b></span>)}<span className="w-grow" style={{ flex: 1 }}></span><span className="w-ab-sevchip"><ABIcons.warn size={12} style={{ color: 'var(--cond)' }} /> {human} need human review</span></div>}

        {!!renders.length && <React.Fragment><div className="w-ab-sec"><span className="w-sec-h" style={{ margin: 0 }}>Client renders</span><span className="w-meta">{Math.min(renders.length, 120)} of {total || renders.length} shown</span><span className="w-grow" style={{ flex: 1 }}></span><div className="w-seg">{families.map((name) => <button key={name} className={family === name ? 'on' : ''} onClick={() => setFamily(name)}>{name}</button>)}</div></div><div className="w-ab-grid" style={{ flexShrink: 0 }}>{shown.slice(0, 120).map((render) => {
          const decision = decisions.get(abKey(render));
          const unseen = codexMode && (!render.qa || render.qa.cropTrusted === false || (decision && decision.source === 'codex_skipped'));
          const name = render.clientName || render.clientId || 'Client render';
          const openRender = () => setView(renders.indexOf(render));
          const openLightbox = (event) => {
            event.preventDefault(); event.stopPropagation();
            const thumbnail = event.currentTarget.parentElement.querySelector('.ab-thumb');
            if (window.openRenderLightbox) window.openRenderLightbox(thumbnail, name);
          };
          return <div key={abKey(render)} data-ab-render-key={abKey(render)} data-ab-render-status={render.qaStatus} style={{ position: 'relative', minWidth: 0 }}><button type="button" className="w-ab-card" onClick={openRender} title={'Open ' + name + ' full screen'} style={{ width: '100%', height: '100%' }}><AbThumbnail render={render} /><span className="w-ab-name">{name}</span><span className="w-ab-fam">{render.clientFamily || 'client'}{render.variant ? ' · ' + render.variant : ''}</span>{unseen ? <span className="w-abp cond sm" title="Unmeasurable; routed to review"><ABIcons.warn size={9} />Unseen</span> : <AbRenderStatus status={render.qaStatus} />}</button>{(render.thumbnailSrc || render.screenshotSrc) && <button type="button" className="w-chip" onClick={openLightbox} data-afterburner-lightbox="1" title={'Open ' + name + ' in the shared image lightbox'} aria-label={'Open ' + name + ' image lightbox'} style={{ position: 'absolute', right: 14, top: 14, zIndex: 3, padding: 5, borderRadius: 6 }}><ABIcons.expand size={11} /></button>}</div>;
        })}</div></React.Fragment>}

        {!!findings.length && <React.Fragment><div className="w-ab-sec" data-afterburner-findings={codexMode ? 'codex-coverage' : 'legacy-claude'}><span className="w-sec-h" style={{ margin: 0 }}>{codexMode ? 'Codex coverage findings' : 'Claude diagnoses'}</span><span className="w-meta">{findings.length} recorded row{findings.length === 1 ? '' : 's'} · stage3</span></div>{findings.map((finding, findingIndex) => {
          const severity = AB_SEVERITY_ORDER.includes(finding.severity) ? finding.severity : 'low';
          const block = abResolvedBlock(finding, B);
          const fix = abFindingFix(finding);
          const scope = chaseFindingScope(B?.blocks, block);
          return <div key={findingIndex} className="w-ab-find" style={{ borderLeftColor: AB_SEVERITY_COLOR[severity], flexShrink: 0 }}>
            <div className="w-ab-find-h"><span className="w-ab-badge" style={{ background: AB_SEVERITY_COLOR[severity] }}>{AB_SEVERITY_LABEL[severity]}</span><b>{finding.clientName || finding.clientId || 'Client'}</b>{(finding.type || finding.issueType || finding.issue_type) && <span className="w-chip" style={{ padding: '3px 8px', fontSize: 10.5, boxShadow: 'none', background: 'var(--sink)' }}>{finding.type || finding.issueType || finding.issue_type}</span>}{finding.confidence != null && <span className="w-meta">conf {Math.round(Number(finding.confidence) * 100)}%</span>}{(finding.needsHumanReview || finding.humanReview) && <span className="w-ab-human"><ABIcons.warn size={10} /> human review</span>}<span className="w-grow" style={{ flex: 1 }}></span>{block && <button className="w-cm-anchor" onClick={() => openBlock(block)}>{blockLabel(block)}</button>}</div>
            <div className="w-ab-sum">{abFindingSummary(finding)}</div>{finding.likelyCause && <span className={'w-ab-prov' + (/merge/i.test(finding.likelyCause) ? ' det' : '')}>{finding.likelyCause}</span>}{fix && <div className="w-ab-fix"><span>Suggested fix</span>{fix}</div>}
            {block && <div className="w-ab-acts"><button className="w-chip" onClick={() => openBlock(block)}><ABIcons.code size={11} /> Open the block</button>
              <button className="w-chip" disabled={!canFix || !scope || !chase.wired || chase.busy || chase.disabled || chase.acceptBlocked} title={!canFix ? evidence.detail : !scope ? 'An authored region or explicit document scope is required' : !chase.wired ? abChaseUnavailable(chase) : !fix ? 'No suggested fix recorded — ask Chase to propose one' : 'Hand this finding to Chase as a staged proposal'}
                onClick={async () => {
                  if (!canFix || !scope || !chase.wired || chase.busy || chase.disabled || chase.acceptBlocked) return;
                  setFixError(null);
                  const key = evidenceRequest.current;
                  const result = await chase.ask(abChaseInstruction(finding, {block}), scope);
                  if (key === evidenceRequest.current && !result?.operation_id) setFixError({ key, findingIndex, message: result?.error?.title || 'Chase did not accept this request. Check scope and worker availability.' });
                }}><ABIcons.jet size={11} /> Fix with Chase</button></div>}
            {fixError?.key === evidenceRequest.current && fixError.findingIndex === findingIndex && <p role="alert">{fixError.message}</p>}
          </div>;
        })}{!chase.wired && findings.some((finding) => abResolvedBlock(finding, B)) && <div className="w-bar-note" data-afterburner-chase="unwired" style={{ marginTop: 10 }}>{abChaseUnavailable(chase)}</div>}</React.Fragment>}

        {!running && !hasResults && !reason && <div className="w-ab-foot" style={{ padding: 16 }}>No Afterburner artifacts have been produced for this build yet.</div>}
        {hasResults && <div className="w-ab-foot">Separate <b>eoa-qa/</b> artifacts — main QA and the pixel diff are untouched by this run.</div>}
      </div>

      {view != null && renders[view] && window.ReactDOM.createPortal(<AbRenderView renders={renders} index={view} setIndex={setView} accent={(c && c.accent) || '#4F9D69'} chase={chase} B={B} decisions={decisions} evidence={{ ...evidence, current: canFix }} recorded={status.recorded === true || status.provider === 'recorded'} onClose={() => setView(null)} onOpenBlock={(id) => {setView(null);openBlock(id);}} />, document.body)}
    </div>
  );
}

const AB_RETAINED_STATES = ['prepared', 'submission_unknown', 'provider_accepted', 'complete', 'failed'];
function rwAbHistory(value, job, workspace) {
  if (!value || value.schema !== 'AfterburnerOperationHistory.v1' || value.job_id !== job || value.workspace_id !== workspace
    || value.recorded !== true || value.deliverable !== false || value.certification !== 'not_certified'
    || !Array.isArray(value.operations) || value.operations.length > 50
    || !(value.next_cursor === null || (typeof value.next_cursor === 'string' && /^[A-Za-z0-9_-]{1,4096}$/.test(value.next_cursor)))) {
    throw Error('Retained Afterburner history could not be verified. Refresh to try again.');
  }
  const ids = new Set();
  for (const row of value.operations) {
    if (row?.schema !== 'AfterburnerRequestStatus.v1' || row.job_id !== job || !AB_DIGEST.test(row.operation_id || '')
      || !AB_DIGEST.test(row.revision_digest || '') || !AB_DIGEST.test(row.input_digest || '')
      || !/^r(?:0|[1-9][0-9]{0,5})$/.test(row.revision || '') || !AB_RETAINED_STATES.includes(row.state)
      || row.provider !== 'recorded' || row.recorded !== true || row.deliverable !== false || row.certification !== 'not_certified'
      || typeof row.created_at !== 'string' || !Number.isFinite(Date.parse(row.created_at)) || ids.has(row.operation_id)) {
      throw Error('A retained Afterburner history entry is invalid. No finding is actionable.');
    }
    ids.add(row.operation_id);
  }
  return value;
}
function rwAbReview(value, job, workspace, operation) {
  const terminal = value && ['complete', 'failed'].includes(value.state);
  if (!value || value.schema !== 'AfterburnerReview.v1' || value.job_id !== job || value.workspace_id !== workspace
    || value.operation_id !== operation || !AB_DIGEST.test(operation || '') || !AB_RETAINED_STATES.includes(value.state)
    || value.provider !== 'recorded' || value.recorded !== true || value.deliverable !== false || value.certification !== 'not_certified'
    || value.qa_verdict !== null || !Array.isArray(value.screenshots) || value.screenshots.length !== 0
    || typeof value.raw_observation_available !== 'boolean' || !['recorded_findings', 'uninterpreted'].includes(value.interpretation)
    || !Array.isArray(value.findings) || value.findings.length > 200
    || (terminal ? !rwAbValidBinding(value) : value.revision_binding !== null)
    || (value.interpretation === 'uninterpreted' && value.findings.length)
    || (value.interpretation === 'recorded_findings' && (!terminal || !value.raw_observation_available))) {
    throw Error('This retained result is invalid or belongs to another source. Refresh retained checks.');
  }
  const ids = new Set(), text = (x, max) => typeof x === 'string' && x.length > 0 && x.length <= max;
  for (const f of value.findings) {
    if (!f || Object.keys(f).sort().join(',') !== 'block_id,client_id,id,severity,suggested_fix,summary'
      || !text(f.id, 128) || ids.has(f.id) || !text(f.client_id, 128) || !AB_SEVERITY_ORDER.includes(f.severity)
      || !text(f.summary, 4000) || !(f.block_id === null || (text(f.block_id, 128) && /^[A-Za-z0-9][A-Za-z0-9._:-]*$/.test(f.block_id)))
      || !(f.suggested_fix === null || text(f.suggested_fix, 8000))) {
      throw Error('A retained finding is malformed. No finding is actionable.');
    }
    ids.add(f.id);
  }
  return value;
}

function RwAfterburnerRetainedReview({ packet, contextKey, job, afterburnerWorkspaceId, editorSession,
  draftDirty, revisionHeadMoved, sessionRefreshing, B, chase, onViewDiff }) {
  const [source, setSource] = React.useState(null), [selected, setSelected] = React.useState(null);
  const [chaseRequest, setChaseRequest] = React.useState(null);
  const key = JSON.stringify([contextKey, packet.operation_id, packet.result_digest]);
  const active = React.useRef(key); active.current = key;
  React.useEffect(() => {
    let alive = true; setSource(null); setSelected(null);
    if (packet.revision_binding && editorSession && window.GooseAPI?.readRevision) {
      Promise.resolve().then(() => window.GooseAPI.readRevision(job.id, editorSession.revision)).then(value => {
        if (alive && active.current === key) setSource({ key, value });
      }, () => { if (alive && active.current === key) setSource(null); });
    }
    return () => { alive = false; };
  }, [key]);
  const evidence = rwAbEvidence(packet, { jobId: job.id, workspaceId: afterburnerWorkspaceId, session: editorSession,
    revision: source?.key === key ? source.value : null, dirty: draftDirty, headMoved: revisionHeadMoved, refreshing: sessionRefreshing });
  const canFix = evidence.current && packet.state === 'complete' && packet.interpretation === 'recorded_findings';
  const block = f => f.block_id && B?.blocks?.some(row => row.id === f.block_id) ? f.block_id : null;
  const disabled = f => !canFix || !chase?.wired || chase.busy || chase.disabled || chase.acceptBlocked || !chaseFindingScope(B?.blocks, f.block_id);
  const open = f => { const id = block(f); if (id) onViewDiff ? onViewDiff(id) : B?.setSel?.(id); };
  const ask = async f => {
    if (disabled(f) || active.current !== key) return;
    setChaseRequest(null);
    const result = await chase.ask('Recorded Afterburner finding (' + f.client_id + '): ' + f.summary + (f.suggested_fix ? '\nSuggested fix: ' + f.suggested_fix : ''),
      chaseFindingScope(B?.blocks, f.block_id));
    if (active.current === key) setChaseRequest({ key, finding: f.id, operation_id: result?.operation_id,
      error: result?.operation_id ? null : result?.error?.title || 'Chase did not accept this request. Check scope and worker availability, then retry.' });
  };
  const finding = selected && packet.findings.find(f => f.id === selected);
  return <div className="w-ab" data-afterburner-retained-review={packet.operation_id}>
    <div className="w-ab-top"><ABIcons.flame size={15} /><b>Retained Afterburner check</b>
      <AbStatusPill status="recorded" label={'Recorded · ' + packet.state.replaceAll('_', ' ')} />
      <span className="w-meta">{packet.revision} · {packet.operation_id.slice(0, 12)}</span></div>
    <AbRevisionEvidence evidence={evidence} recorded={true} />
    <div className="w-ab-scroll">
      <div className="w-callout info"><ABIcons.warn size={15} /><span>No live provider transport or screenshots are qualified here. These retained observations are not an external-render verdict, approval or certification.</span></div>
      <p className="w-meta">{packet.state === 'prepared' ? 'Saved only — this operation has not been queued or submitted.'
        : packet.state === 'submission_unknown' ? 'Submission outcome unknown. Do not relaunch; recover the original operation.'
        : packet.state === 'provider_accepted' ? 'Recorded provider acceptance is retained; no final observation yet.'
        : packet.interpretation === 'uninterpreted' ? packet.raw_observation_available
          ? 'Observation bytes retained, but this payload has no supported findings schema. No findings or verdict are inferred.'
          : 'Only the historical observation digest is retained; the original payload is unavailable. No findings or verdict are inferred.'
        : packet.findings.length + ' structured recorded findings. Zero findings would not mean a QA pass.'}</p>
      {packet.findings.map(f => <div key={f.id} className="w-ab-find" style={{ borderLeftColor: AB_SEVERITY_COLOR[f.severity] }}>
        <div className="w-ab-find-h"><span className="w-ab-badge" style={AB_PORTAL_SEVERITY_STYLE[f.severity]}>{AB_SEVERITY_LABEL[f.severity]}</span><b>{f.client_id}</b></div>
        <div className="w-ab-sum">{f.summary}</div>{f.suggested_fix && <div className="w-ab-fix"><span>Suggested fix</span>{f.suggested_fix}</div>}
        <div className="w-ab-acts"><button className="w-chip" onClick={() => setSelected(f.id)}>Inspect finding</button>
          {block(f) && <button className="w-chip" onClick={() => open(f)}>Open the block</button>}
          <button className="w-chip" disabled={disabled(f)} title={!f.suggested_fix ? 'No suggested fix recorded — ask Chase to propose one' : 'Hand the suggested fix to Chase as a staged proposal'} onClick={() => ask(f)}>Fix with Chase</button></div>
        {!chaseFindingScope(B?.blocks, f.block_id) && <p className="w-meta">No matching authored region; write scope is not inferred. Explicitly select Whole file in Chase if needed.</p>}
        {chaseRequest?.key === key && chaseRequest.finding === f.id && <p role={chaseRequest.error ? 'alert' : 'status'}>{chaseRequest.error || chaseFindingProgress(chase, chaseRequest.operation_id)}</p>}
      </div>)}
    </div>
    {finding && window.ReactDOM.createPortal(<RwAfterburnerFindingDialog finding={finding} evidence={evidence}
      message={chaseRequest?.key === key && chaseRequest.finding === finding.id ? chaseRequest.error || chaseFindingProgress(chase, chaseRequest.operation_id) : null}
      error={!!chaseRequest?.error} disabled={disabled(finding)} onAsk={() => ask(finding)} onClose={() => setSelected(null)} />, document.body)}
  </div>;
}
function RwAfterburnerFindingDialog({ finding, evidence, disabled, onAsk, onClose, message, error }) {
  const dialog = React.useRef(null);
  React.useEffect(() => {
    const previous = document.activeElement; dialog.current?.focus();
    const escape = event => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('keydown', escape); previous?.focus?.(); };
  }, []);
  return <div className="w-abv w-review-theme" role="dialog" aria-modal="true" aria-label="Retained Afterburner finding" tabIndex="-1" ref={dialog}>
    <header className="w-abv-h"><b>{finding.client_id} · {AB_SEVERITY_LABEL[finding.severity]}</b><span style={{ flex: 1 }} />
      <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button></header>
    <div className="w-ab-scroll"><AbRevisionEvidence evidence={evidence} recorded={true} /><div className="w-ab-find">
      <p>{finding.summary}</p><p>{finding.suggested_fix || 'No suggested fix was retained.'}</p>
      {message && <p role={error ? 'alert' : 'status'}>{message}</p>}
      <p className="w-meta">Chase stages a proposal only. Acceptance and full-email QA remain separate from approval.</p>
      <button className="btn btn-primary" disabled={disabled} onClick={onAsk}>Ask Chase to fix</button></div></div>
  </div>;
}
function RwAfterburnerHistoryView(props) {
  const { job, afterburnerWorkspaceId, afterburnerContextKey, editorSession, draftDirty, revisionHeadMoved, sessionRefreshing } = props;
  const contextKey = JSON.stringify([job.id, afterburnerWorkspaceId, afterburnerContextKey, editorSession?.revision,
    editorSession?.revision_digest, editorSession?.projection?.html_digest, draftDirty, revisionHeadMoved, sessionRefreshing]);
  const [history, setHistory] = React.useState(null), [selection, setSelection] = React.useState('legacy');
  const [review, setReview] = React.useState(null), [error, setError] = React.useState(null), [busy, setBusy] = React.useState(false);
  const request = React.useRef({ key: contextKey, sequence: 0 }); request.current.key = contextKey;
  const currentHistory = history?.key === contextKey ? history.value : null;
  const currentReview = review?.key === contextKey && review.id === selection ? review.value : null;
  const read = async (cursor = null) => {
    const sequence = ++request.current.sequence; setBusy(true); setError(null); setReview(null);
    try {
      const result = rwAbHistory(await window.GooseAPI.afterburnerHistory(job.id, cursor), job.id, afterburnerWorkspaceId);
      if (request.current.key !== contextKey || request.current.sequence !== sequence) return;
      const combined = cursor ? [...(currentHistory?.operations || []), ...result.operations] : result.operations;
      if (new Set(combined.map(x => x.operation_id)).size !== combined.length) throw Error('History changed while paging. Refresh retained checks.');
      const rows = combined.slice(-200); // bounded browser window, not a cutoff on retained history
      setHistory({ key: contextKey, value: { ...result, operations: rows } });
      const id = cursor && (selection === 'legacy' || rows.some(row => row.operation_id === selection))
        ? selection : rows[0]?.operation_id || 'legacy'; setSelection(id);
      if (id !== 'legacy') {
        const value = rwAbReview(await window.GooseAPI.afterburnerReview(job.id, id), job.id, afterburnerWorkspaceId, id);
        if (request.current.key === contextKey && request.current.sequence === sequence) setReview({ key: contextKey, id, value });
      }
    } catch (e) {
      if (request.current.key === contextKey && request.current.sequence === sequence) setError(e.message || 'Retained checks unavailable. Refresh to try again.');
    } finally { if (request.current.key === contextKey && request.current.sequence === sequence) setBusy(false); }
  };
  React.useEffect(() => {
    setHistory(null); setReview(null); setSelection('legacy'); setBusy(false); setError(null);
    if (afterburnerWorkspaceId) read();
    return () => { request.current.sequence++; };
  }, [contextKey]);
  const choose = async id => {
    const sequence = ++request.current.sequence; setSelection(id); setReview(null); setError(null); setBusy(false);
    if (id === 'legacy') return;
    if (!currentHistory?.operations.some(row => row.operation_id === id)) { setError('Unknown retained operation. Refresh retained checks.'); return; }
    setBusy(true);
    try {
      const value = rwAbReview(await window.GooseAPI.afterburnerReview(job.id, id), job.id, afterburnerWorkspaceId, id);
      if (request.current.key === contextKey && request.current.sequence === sequence) setReview({ key: contextKey, id, value });
    } catch (e) { if (request.current.key === contextKey && request.current.sequence === sequence) setError(e.message || 'Retained check unavailable.'); }
    finally { if (request.current.key === contextKey && request.current.sequence === sequence) setBusy(false); }
  };
  return <div className="w-ab-history" data-afterburner-history="retained">
    <div className="w-ab-top"><label>Saved checks <select aria-label="Saved Afterburner check" value={currentHistory ? selection : 'legacy'} disabled={busy || !currentHistory} onChange={e => choose(e.target.value)}>
      <option value="legacy">Legacy job-level results</option>{currentHistory?.operations.map(row => <option key={row.operation_id} value={row.operation_id}>{row.revision} · {row.state.replaceAll('_', ' ')} · {row.operation_id.slice(0, 10)}</option>)}
    </select></label><button className="w-chip" disabled={busy || !afterburnerWorkspaceId} onClick={() => read()}>Refresh retained checks</button>
      {currentHistory?.next_cursor && <button className="w-chip" disabled={busy} onClick={() => read(currentHistory.next_cursor)}>Older checks</button>}</div>
    {currentHistory?.operations.length >= 200 && <p className="w-meta">Showing a rolling window of 200 checks. Keep loading older checks, or refresh to return to the newest.</p>}
    {error && <div className="w-abr bad" role="alert">{error}</div>}
    {busy && <p role="status">Reading retained checks…</p>}
    {!busy && currentHistory?.operations.length === 0 && <p className="w-meta">No revision-bound checks have been retained for this build.</p>}
    {selection === 'legacy' ? <RwAfterburnerLegacyView {...props} /> : currentReview
      ? <RwAfterburnerRetainedReview key={contextKey + ':' + selection} {...props} contextKey={contextKey} packet={currentReview} />
      : !busy && <p className="w-meta">No verified retained result is available. Refresh or select another check.</p>}
  </div>;
}
function RwAfterburnerView(props) {
  return props.isLive && window.GooseAPI?.afterburnerHistory && window.GooseAPI?.afterburnerReview
    ? <RwAfterburnerHistoryView {...props} /> : <RwAfterburnerLegacyView {...props} />;
}

Object.assign(window, { RwAfterburnerView, AbRenderView, abIsCodex, AB_PHASE_LABEL });
